import cn from 'classnames'
import {
  StyledVariable,
  TEXT_KEY,
  URGENCY_TEXT_VARIABLE_NAME,
  UrgencyEntries,
  useUrgency,
} from '@mc/feature-variables'
import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import Sticky from 'components/Sticky'
import { PromoVariables } from 'pages/certificates/promo'
import { TrackingAttributes, TrackVisible } from 'pages/certificates/tracking'
import { SignUpButton } from '../SignUpButton'

type FooterPayload = NonNullable<PromoVariables['footer']>

type CommonFooterProps = {
  checkoutHref?: string
  ctaText?: string
  // Overrides the Segment purchaseClick `action` (an analytics value, not
  // user-facing copy — intentionally not localized)
  ctaAction?: string
  // 'end' clusters text next to the CTA (homepage/CLP treatment);
  // 'start' keeps the text on the left with the CTA on the right
  textAlign?: 'start' | 'end'
}

// Promo states: payload-driven footer (styled text + optional urgency), a
// terms link, and the 'promo footer' tracking location / promoFooterViewed
type PromoFooterProps = CommonFooterProps & {
  variant?: 'promo'
  // Only `terms` is read; PromoVariables['promo'] satisfies this shape.
  // Absent for discounted states without a payload promo block, which link the
  // generic terms page instead
  promo?: Pick<PromoVariables['promo'], 'terms'>
  footer: FooterPayload
  subText?: never
}

// Non-promo states: plain copy with an optional small line, no terms link,
// and the 'sticky-footer' tracking location / stickyFooterViewed
type DefaultFooterProps = CommonFooterProps & {
  variant: 'default'
  promo?: never
  footer: Pick<FooterPayload, 'text'>
  subText?: string
}

type Props = PromoFooterProps | DefaultFooterProps

const FOOTER_URGENCY_CONFIG: UrgencyEntries = {
  urgencyText: {
    variableName: URGENCY_TEXT_VARIABLE_NAME,
    variableKey: TEXT_KEY,
  },
}

// The single certificates sticky footer, shared by the promo and non-promo
// states (MCCERTS-2728): promo states pass the payload footer (styled text +
// urgency), non-promo states pass plain copy via footer.text/subText
export const StickyFooter = ({
  variant = 'promo',
  promo,
  footer,
  checkoutHref,
  ctaText,
  ctaAction,
  textAlign = 'start',
  subText,
}: Props) => {
  const { t } = useTranslation('@mc/certificates')
  const urgencies = useUrgency({
    entries: FOOTER_URGENCY_CONFIG,
    variables: footer,
  })
  const isPromo = variant === 'promo'

  return (
    <TrackingAttributes location={isPromo ? 'promo footer' : 'sticky-footer'}>
      <Sticky position='bottom' config={{ bottomBuffer: 300, topBuffer: 100 }}>
        <TrackVisible
          event={isPromo ? 'promoFooterViewed' : 'stickyFooterViewed'}
        >
          <div
            data-testid='sticky-footer'
            className='bg-c-base-100 py-4 shadow-c-footer'
          >
            <div
              className={cn(
                'container flex items-center gap-3 justify-between',
                {
                  'md:justify-end': textAlign === 'end',
                },
              )}
            >
              <div
                className={cn('flex flex-col lg:flex-row md:items-baseline', {
                  'lg:justify-end md:mr-2': textAlign === 'end',
                })}
              >
                <p className='c-banner-heading text-c-text-dark m-0 text-balance'>
                  <StyledVariable text={footer.text} />
                </p>
                <div className='flex items-center gap-1 c-body-small text-c-text-tint-3 lg:ml-2'>
                  {urgencies.urgencyText ? (
                    <span>
                      <StyledVariable text={urgencies.urgencyText} />
                    </span>
                  ) : (
                    subText && <span>{subText}</span>
                  )}
                  {isPromo && (
                    <a
                      className='mc-text--link text-c-text-tint-3'
                      href={promo?.terms || paths.terms}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {t('marketingPages.promo.termsLink')}
                    </a>
                  )}
                </div>
              </div>
              <SignUpButton
                className='shrink-0'
                arrow
                href={checkoutHref}
                text={ctaText}
                action={ctaAction}
              />
            </div>
          </div>
        </TrackVisible>
      </Sticky>
    </TrackingAttributes>
  )
}
